import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Users } from 'lucide-react';
import { useCustomersInGroup } from '~/hooks/use-customer-groups';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	exprId: number | null;
	groupName?: string;
}

/** Read-only preview of the customers currently matching a group's dynamic expression. */
export function MembersDialog({ open, onOpenChange, exprId, groupName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading } = useCustomersInGroup(open ? exprId : null);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t('customers.groups.members.title')}</DialogTitle>
					<DialogDescription>{groupName || t('customers.groups.members.subtitle')}</DialogDescription>
				</DialogHeader>
				<div className="max-h-[60vh] overflow-auto rounded-md border border-border bg-surface">
					{isLoading ? (
						<div className="flex flex-col gap-2 p-3">
							{Array.from({ length: 6 }).map((_, i) => (
								<Skeleton key={i} className="h-9 w-full rounded-md" />
							))}
						</div>
					) : !data || data.length === 0 ? (
						<div className="flex flex-col items-center gap-3 py-12 text-center">
							<Users size={36} className="text-foreground-disabled" strokeWidth={1.25} />
							<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
						</div>
					) : (
						<table className="w-full border-collapse text-sm">
							<thead className="bg-elevated text-xs text-foreground-muted">
								<tr className="border-b border-border">
									<th className="px-3 py-2 text-left font-medium">{t('customers.groups.members.name')}</th>
									<th className="px-3 py-2 text-left font-medium">{t('customers.groups.members.email')}</th>
								</tr>
							</thead>
							<tbody>
								{data.map(c => (
									<tr key={c.id} className="border-b border-border/60">
										<td className="px-3 py-2 text-foreground">{[c.fn, c.ln].filter(Boolean).join(' ') || '—'}</td>
										<td className="px-3 py-2 text-foreground-muted">{c.eml}</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
